import React from 'react'
import clsx from 'clsx'
import { ConnectDragSource } from 'react-dnd'
import { MdDragHandle } from 'react-icons/md'
import { Hierarchy, MessageText } from 'iconsax-react'
import { useTheme } from '@mui/material'
import { useAppSelector } from '@SonarCX/hooks/redux'

import { formatDateTask } from '@SonarCX/timeUtilities'
import { selectTaskPriority } from '@SonarCX/slices/priority'
import { selectTaskStatus } from '@SonarCX/slices/taskStatus'
import {
  TabState,
  useTaskCardState
} from '@SonarCX/providers/TaskCardStateProvider'
import useTaskPermissions from '@SonarCX/hooks/permissions/useTaskPermissions'
import { useTaskComments } from '@SonarCX/providers/TaskCommentsProvider'
import useStyles from './styles'

type DetailsProps = {
  task: LocalTask
  dragRef?: ConnectDragSource
  hideDragHandle?: boolean
}

const Details: React.FC<DetailsProps> = ({ task, dragRef, hideDragHandle }) => {
  const theme = useTheme()

  const { priorities } = useAppSelector(selectTaskPriority)
  const { statuses } = useAppSelector(selectTaskStatus)

  const { canEdit } = useTaskPermissions(task)
  const { counts } = useTaskComments()
  const { tabState, setTabState } = useTaskCardState()

  const count = React.useMemo(() => task.sub_tasks ?? 0, [task.sub_tasks])
  const comments = React.useMemo(
    () => Math.max(counts[task.id] ?? 0, task?.comments ?? 0),
    [counts, task.id, task.comments]
  )
  const tab = React.useMemo(
    () => tabState[task.id] ?? TabState.NONE,
    [tabState, task.id]
  )

  const priority = React.useMemo(() => {
    if (!task.priority_id) {
      return {
        name: 'No Priority',
        color: 'grey'
      }
    }

    const foundPriority = priorities.find(p => p.id === task.priority_id)

    if (!foundPriority) {
      return {
        name: 'Unknown Priority',
        color: 'grey'
      }
    }

    return {
      name: foundPriority.name,
      color: `#${foundPriority.colour ?? '000000'}`
    }
  }, [task.priority_id, priorities])

  const status = React.useMemo(() => {
    if (!task.status_id) {
      return {
        name: 'No Status',
        color: 'grey'
      }
    }

    const foundStatus = statuses.find(s => s.id === task.status_id)

    if (!foundStatus) {
      return {
        name: 'Unknown Status',
        color: 'grey'
      }
    }

    return {
      name: foundStatus.name,
      color: `#${foundStatus.colour ?? '000000'}`
    }
  }, [task.status_id, statuses])

  const isOverdue = React.useMemo(() => {
    if (!task.due_date) {
      return false
    }

    if (task.completed_at) {
      return false
    }

    return new Date(task.due_date) < new Date()
  }, [task.due_date, task.completed_at])

  const classes = useStyles()

  const handleSelect = React.useCallback(
    (tab: TabState) => {
      setTabState(task, tab)
    },
    [setTabState, task]
  )

  return (
    <div className={classes.root}>
      <div className={classes.stats}>
        <div className={classes.stat}>
          <div>Priority</div>

          <div style={{ color: priority.color }}>{priority.name}</div>
        </div>

        <div className={classes.stat}>
          <div>Status</div>

          <div style={{ color: status.color }}>{status.name}</div>
        </div>

        <div className={classes.stat}>
          <div>Due</div>

          <div
            style={{ color: isOverdue ? theme.palette.indicator.red : 'black' }}
          >
            {formatDateTask(task.due_date)}
          </div>
        </div>

        {task.tags.length > 0 && (
          <div className={classes.tagList}>
            {task.tags.map((tag, index) => (
              <div key={`tag-card-${tag}-${index}`} className={classes.tag}>
                {tag}
              </div>
            ))}
          </div>
        )}
      </div>

      <div onClick={e => e.stopPropagation()} className={classes.controls}>
        {canEdit && !hideDragHandle && (
          <div ref={dragRef} className={classes.dragHandle}>
            <MdDragHandle className={classes.dragIcon} />
          </div>
        )}

        <div
          onClick={() => handleSelect(TabState.SUBTASKS)}
          className={clsx(
            classes.count,
            tab === TabState.SUBTASKS && classes.countSelected
          )}
          style={{ marginRight: '10px' }}
        >
          <span className='count'>{count}</span>

          <span className='icon'>
            <Hierarchy size={theme.size.sm} className={classes.icon} />
          </span>
        </div>

        <div
          onClick={() => handleSelect(TabState.COMMENTS)}
          className={clsx(
            classes.count,
            tab === TabState.COMMENTS && classes.countSelected
          )}
          style={{ marginRight: '10px' }}
        >
          <span className='count'>{comments}</span>

          <span className='icon'>
            <MessageText size={theme.size.sm} />
          </span>
        </div>
      </div>
    </div>
  )
}

export default Details
